import React, { useEffect, useState, useRef } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { useLocalizedNavigate } from '../hooks/useLocalizedNavigate';
import { packageAPI } from '../services/api';

import { useI18n } from '../i18n/i18nContext';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { useNoIndex } from '../hooks/useNoIndex';
import { BookingCartPackageItem, useBookingCartStore } from '../stores/bookingCartStore';
import { SuggestedServices } from '../components/SuggestedServices';
import { BookingCart } from '../components/BookingCart';
import { Toast } from '../components/Toast';
import { Package, Service, TimeSlot } from '@shared/types';
import { pushToDataLayer, clearEcommerce, formatPackageItem } from '../utils/dataLayer';
import ServiceBooking from 'src/components/ServiceBooking';
import { formatInTimeZone, fromZonedTime } from 'date-fns-tz';
import { addMinutes, format } from 'date-fns';
import { TALLINN_TIMEZONE } from '@shared/constants';
import { getTranslatedField } from 'src/utils/translationUtils';

interface LocationState {
  bookingId?: string;
  currentDate?: string;
  currentTime?: string;
  preselectedDate?: Date;
  preselectedTime?: string;
  isChanging?: boolean;
  cartItemId?: string;
  preselectedServices?: { serviceId: string; startTime: string }[];
}

interface TimeInfo {
  selectedSlot: string;
  selectedDate: Date | undefined;
  timeSlots: TimeSlot[];
}

type SelectedServicesTimeInfo = Record<string, TimeInfo>;

const BookingPackageCalendarPage: React.FC = () => {
  const { packageId } = useParams<{ packageId: string }>();
  const navigate = useLocalizedNavigate();
  const { t, language } = useI18n();
  const { items, addBooking, updateBooking } = useBookingCartStore();
  const location = useLocation();

  useDocumentTitle(t.booking.selectDateTime);
  useNoIndex();

  const [pkg, setPkg] = useState<Package | null>(null);

  const [loading, setLoading] = useState(true);
  const [showCartNotification, setShowCartNotification] = useState(false);
  const [lastOperationWasChange, setLastOperationWasChange] = useState(false);
  const [allPackageServicesBooked, setAllPackageServicesBooked] = useState(false);

  const [selectedServicesTimeInfo, setSelectedServicesTimeInfo] =
    useState<SelectedServicesTimeInfo>({});

  const hasTrackedViewItem = useRef(false);

  const locationState = location.state as LocationState | null;
  const isChanging = locationState?.isChanging || false;
  const cartItemIdToChange = locationState?.cartItemId;

  useEffect(() => {
    if (packageId) {
      fetchPackage();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [packageId]);

  useEffect(() => {
    if (!pkg) {
      setAllPackageServicesBooked(false);
      return;
    }

    const packageCartItem = items.find((item) => item.type === 'package' && item.id === pkg.id);
    setAllPackageServicesBooked(
      (packageCartItem?.services?.length || 0) === (pkg.services?.length || 0)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, pkg]);

  useEffect(() => {
    if (pkg && !hasTrackedViewItem.current) {
      hasTrackedViewItem.current = true;
      clearEcommerce();
      pushToDataLayer('view_item', {
        content: { category: 'booking_package' },
        ecommerce: {
          items: [formatPackageItem(pkg)],
        },
      });
    }
  }, [pkg]);

  const fetchPackage = async () => {
    if (!packageId) return;

    try {
      const response = await packageAPI.getById(packageId);
      setPkg(response.data);
    } catch (err) {
      console.error(t.console.errors.fetchingService, err);
      navigate('/book');
    } finally {
      setLoading(false);
    }
  };

  const hasAllPackageServicesSelected = () => {
    if (!pkg?.services?.length) return false;

    return pkg.services.every((service) => {
      const timeInfo = selectedServicesTimeInfo[service.id];
      if (!timeInfo?.selectedSlot || !timeInfo.selectedDate) return false;

      return timeInfo.timeSlots.some((slot) => slot.id === timeInfo.selectedSlot);
    });
  };

  const handleAddToCart = () => {
    if (!pkg || !hasAllPackageServicesSelected()) return;

    const selectedServiceBookings = Object.entries(selectedServicesTimeInfo)
      .map(([serviceId, timeInfo]) => {
        const { selectedSlot, timeSlots, selectedDate } = timeInfo;
        if (!selectedSlot || !selectedDate) return null;

        const service = pkg.services?.find((s) => s.id === serviceId);
        if (!service) return null;

        const slot = timeSlots.find((s) => s.id === selectedSlot);
        if (!slot) return null;

        const dateStr = format(selectedDate, 'yyyy-MM-dd');
        const timeStr = slot.time;
        const dateTimeStr = `${dateStr} ${timeStr}`;
        const startDateTime = fromZonedTime(dateTimeStr, TALLINN_TIMEZONE);
        const endDateTime = addMinutes(startDateTime, service.duration);

        return {
          service,
          dateStr,
          timeStr,
          startDateTime,
          endDateTime,
          bookingService: {
            serviceId: service.id,
            serviceName: getTranslatedField(service, 'name', language),
            serviceNameEn: service.name,
            startTime: startDateTime.toISOString(),
            endTime: endDateTime.toISOString(),
            duration: service.duration,
          },
        };
      })
      .filter((item): item is NonNullable<typeof item> => Boolean(item));

    if (selectedServiceBookings.length === 0) return;

    const sortedByStart = [...selectedServiceBookings].sort(
      (a, b) => a.startDateTime.getTime() - b.startDateTime.getTime()
    );
    const packageStartTime = sortedByStart[0].startDateTime;
    const packageEndTime = sortedByStart[sortedByStart.length - 1].endDateTime;

    const bookingDetails: Omit<BookingCartPackageItem, 'cartItemId' | 'quantity'> = {
      type: 'package',
      id: pkg.id,
      name: getTranslatedField(pkg, 'name', language),
      nameEn: pkg.name,
      price: pkg.price || 0,
      startTime: packageStartTime.toISOString(),
      endTime: packageEndTime.toISOString(),
      duration: pkg.duration_minutes,
      services: selectedServiceBookings.map((entry) => entry.bookingService),
    };

    const existingPackageItem = items.find((item) => item.type === 'package' && item.id === pkg.id);
    if (isChanging && cartItemIdToChange) {
      updateBooking(cartItemIdToChange, bookingDetails);
      setLastOperationWasChange(true);
    } else if (existingPackageItem && existingPackageItem.type === 'package') {
      updateBooking(existingPackageItem.cartItemId, bookingDetails);
      setLastOperationWasChange(true);
    } else {
      addBooking(bookingDetails);
      setLastOperationWasChange(false);
    }

    clearEcommerce();
    const firstBooking = selectedServiceBookings[0];
    const packageVariant = `${firstBooking.dateStr} ${firstBooking.timeStr}`;
    pushToDataLayer('add_to_cart', {
      content: { category: 'booking_package' },
      ecommerce: {
        items: [formatPackageItem(pkg, packageVariant)],
      },
    });

    setShowCartNotification(true);
    setSelectedServicesTimeInfo({});
  };

  const areSelectedSlotsAlreadyBooked = () => {
    if (!selectedServicesTimeInfo || Object.keys(selectedServicesTimeInfo).length === 0)
      return false;

    let areSelectedSlotsAlreadyBooked = false;

    for (const [serviceId, timeInfo] of Object.entries(selectedServicesTimeInfo)) {
      const { selectedSlot, timeSlots, selectedDate } = timeInfo;
      if (!selectedSlot || !selectedDate) continue;

      const slot = timeSlots.find((s) => s.id === selectedSlot);
      if (!slot) continue;

      const selectedDateString = format(selectedDate, 'yyyy-MM-dd');

      const isServiceSlotBooked = items.some((item) => {
        if (item.type === 'service') {
          const itemDateString = formatInTimeZone(
            new Date(item.startTime),
            TALLINN_TIMEZONE,
            'yyyy-MM-dd'
          );
          const itemTimeString = formatInTimeZone(
            new Date(item.startTime),
            TALLINN_TIMEZONE,
            'HH:mm'
          );

          return (
            item.id === serviceId &&
            itemDateString === selectedDateString &&
            itemTimeString === slot.time &&
            item.cartItemId !== cartItemIdToChange
          );
        }

        return item.services.some((pkgService) => {
          const itemDateString = formatInTimeZone(
            new Date(pkgService.startTime),
            TALLINN_TIMEZONE,
            'yyyy-MM-dd'
          );
          const itemTimeString = formatInTimeZone(
            new Date(pkgService.startTime),
            TALLINN_TIMEZONE,
            'HH:mm'
          );

          return (
            pkgService.serviceId === serviceId &&
            itemDateString === selectedDateString &&
            itemTimeString === slot.time &&
            item.cartItemId !== cartItemIdToChange
          );
        });
      });

      areSelectedSlotsAlreadyBooked = areSelectedSlotsAlreadyBooked || isServiceSlotBooked;

      if (areSelectedSlotsAlreadyBooked) {
        return true;
      }
    }

    return areSelectedSlotsAlreadyBooked;
  };

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="text-gray-500">{t.common.loading}</div>
      </div>
    );
  }

  if (!pkg) {
    return null;
  }

  const isAddToCartEnabled = hasAllPackageServicesSelected() && !areSelectedSlotsAlreadyBooked();

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
      {showCartNotification && (
        <Toast
          message={
            lastOperationWasChange
              ? t.cart.bookingChanged || 'Booking has been changed'
              : t.cart.addedToCart
          }
          type="success"
          onClose={() => setShowCartNotification(false)}
        />
      )}

      <div className="mb-8">
        <h1 className="mb-2 text-3xl font-bold text-gray-900">{t.booking.title}</h1>
        <p className="text-lg text-gray-600">{t.booking.step2ChooseDateTime}</p>
        <p className="text-md mt-2 text-gray-600">{t.booking.bookAllSessions}</p>
      </div>

      <div className="grid grid-cols-1 gap-8">
        <div className="space-y-8 lg:space-y-4">
          {pkg?.services?.map((service: Service) => (
            <ServiceBooking
              key={service.id}
              service={service}
              setSelectedServicesTimeInfo={setSelectedServicesTimeInfo}
              selectedServicesTimeInfo={selectedServicesTimeInfo}
            />
          ))}

          {/* Action Buttons - Below date/time selection, spanning 4 columns */}
          <div className="my-4 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between lg:col-span-4 lg:col-start-4">
            <button
              onClick={() => navigate('/book')}
              className="w-full rounded-md border border-gray-300 px-6 py-2 font-medium text-gray-700 hover:bg-gray-50 sm:w-auto"
            >
              {t.cart.addAnotherService}
            </button>

            <button
              onClick={handleAddToCart}
              disabled={!isAddToCartEnabled}
              className={`w-full rounded-md px-6 py-2 font-medium sm:w-auto ${
                isAddToCartEnabled
                  ? 'bg-blue-600 text-white hover:bg-blue-700'
                  : 'cursor-not-allowed bg-gray-300 text-gray-500'
              }`}
            >
              {isChanging ? t.cart.confirmChange : t.cart.addToCart}
            </button>
          </div>

          {/* Booking Cart - Below action buttons, spanning 4 columns */}
          {items.length > 0 && (
            <div className="lg:col-span-4 lg:col-start-4">
              <BookingCart hideCheckoutButton={!allPackageServicesBooked} />
            </div>
          )}

          <SuggestedServices />
        </div>
      </div>
    </div>
  );
};

export default BookingPackageCalendarPage;
